import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { experienceAPI } from '../../lib/api';
import { DEFAULT_EXPERIENCE_PREFS, EXPERIENCE_PREFS_STORAGE_KEY as KEY } from '../prefs/defaultPrefs';

// Loads/persists experience_preferences. Backend is the source of truth; a namespaced
// localStorage mirror gives instant pre-auth application (same pattern as SeasonContext).
function loadLocal() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULT_EXPERIENCE_PREFS;
    return { ...DEFAULT_EXPERIENCE_PREFS, ...JSON.parse(raw) };
  } catch (e) {
    return DEFAULT_EXPERIENCE_PREFS;
  }
}

function saveLocal(prefs) {
  try {
    localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch (e) {
    /* localStorage unavailable (private mode): degrade to in-memory only */
  }
}

export function useExperiencePrefs() {
  const { isAuthenticated } = useAuth();
  const [prefs, setPrefs] = useState(loadLocal);
  const [loaded, setLoaded] = useState(false);

  // On auth, reconcile with the backend (source of truth). Read-only; failure keeps the
  // calm local defaults (getPreferences opts out of the 401 auto-logout).
  useEffect(() => {
    let cancelled = false;
    if (!isAuthenticated) {
      // Logged out (or never logged in): wipe the device-shared mirror so the next user on
      // a shared browser never inherits the previous user's consent/quiet/notification state.
      setPrefs(DEFAULT_EXPERIENCE_PREFS);
      try { localStorage.removeItem(KEY); } catch (e) { /* private mode */ }
      setLoaded(true);
      return () => { cancelled = true; };
    }
    experienceAPI
      .getPreferences()
      .then((res) => {
        const p = res && res.data && res.data.preferences;
        if (!cancelled && p) setPrefs((prev) => ({ ...prev, ...p }));
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // Persist the mirror on every change.
  useEffect(() => { saveLocal(prefs); }, [prefs]);

  // Optimistic update + persist to backend when authenticated.
  const updatePrefs = useCallback(async (patch) => {
    setPrefs((prev) => ({ ...prev, ...patch }));
    if (!isAuthenticated) return true;
    try {
      const res = await experienceAPI.updatePreferences(patch);
      const p = res && res.data && res.data.preferences;
      if (p) setPrefs((prev) => ({ ...prev, ...p }));
      return true;
    } catch (e) {
      return false; // keep the optimistic value; the UI may surface the failure
    }
  }, [isAuthenticated]);

  return { prefs, updatePrefs, loaded };
}
